import TicketAddIcon from "@atlas/uicorn/lib/Icon/TicketAddIcon";
import React, { useMemo, useState } from "react";
import useRefCallback from "use-ref-callback";
import { AppSettings } from "../../../lib/get-app-settings";
import styles from "../../../styles/header-actions.module.css";
import Button from "@atlas/uicorn/lib/Control/Button";
import CreateTicketModal from "../create-ticket-modal";
import ContextMenu from "@atlas/uicorn/lib/Control/ContextMenu";
import ChevronIcon from "@atlas/uicorn/lib/Icon/ChevronIcon";
import { TOptionsListItem } from "@atlas/uicorn/lib/Control/OptionsList";
import { TSupportPortalForm } from "../../../lib/load-company-settings";
import useConversationsListContext, {
  ConversationTab,
} from "../customer-portal-context";
import t from "../../../texts/t";
import FilterMenu from "./filter-menu";
import SortMenu from "./sort-menu";
import { TConversationListFilter } from "../../../lib/load-conversations";

const FormSelector = function FormSelector({
  formOptions,
  onFormSelect,
  language,
}: {
  formOptions: TFormOption[];
  onFormSelect: (formKey: TFormOption["key"]) => void;
  language?: string;
}) {
  const createTicketLabel = t("New ticket", language);

  if (formOptions.length === 1) {
    return (
      <Button
        type="tertiary"
        prefix={<TicketAddIcon />}
        className={styles.newTicketButton}
        onClick={() => onFormSelect(formOptions[0].key)}
      >
        {createTicketLabel}
      </Button>
    );
  }

  return (
    <ContextMenu
      items={formOptions}
      onChoose={(form) => onFormSelect((form as TFormOption).key)}
    >
      {({ ref, opened, toggle }) => (
        <Button
          ref={ref}
          type="tertiary"
          prefix={<TicketAddIcon />}
          className={styles.newTicketButton}
          onClick={toggle}
          suffix={<ChevronIcon dir={opened ? "up" : "down"} />}
        >
          {createTicketLabel}
        </Button>
      )}
    </ContextMenu>
  );
};

const HeaderActions = function HeaderActions({
  appSettings,
  tab,
  language,
  isVisitor,
}: THeaderActionsProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<TSupportPortalForm>();
  const { filters, setFilter, sorts, setSort } = useConversationsListContext();

  const showCreateTicketButton = useMemo(() => {
    if (
      isVisitor &&
      !appSettings.supportPortalEnableTicketCreationForVisitors
    ) {
      return false;
    }

    return appSettings.supportPortalForms.length > 0;
  }, [appSettings, isVisitor]);

  const formOptions: TFormOption[] = appSettings.supportPortalForms.map(
    (form) => ({
      key: form.key,
      label: form.title,
    })
  );

  const filter = filters[tab];
  const sort = sorts[tab];

  const handleFormSelect = (formKey: string) => {
    const form = appSettings.supportPortalForms.find((f) => f.key === formKey);
    if (!form) return;

    setIsCreateModalOpen(true);
    setSelectedForm(form);
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleFilterChange = useRefCallback(
    (filter?: TConversationListFilter) => setFilter(tab, filter)
  );

  const handleSortChange = useRefCallback((sort: string) => setSort(tab, sort));

  return (
    <>
      <div className={styles.actions}>
        {showCreateTicketButton && (
          <>
            <FormSelector
              formOptions={formOptions}
              onFormSelect={handleFormSelect}
              language={appSettings.language}
            />
            <div className={styles.divider} />
          </>
        )}
        <SortMenu sort={sort} onChange={handleSortChange} language={language} />
        <FilterMenu
          filter={filter}
          onChange={handleFilterChange}
          language={language}
        />
      </div>

      {isCreateModalOpen && selectedForm && (
        <CreateTicketModal
          appSettings={appSettings}
          formConfig={selectedForm}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default HeaderActions;

type TFormOption = TOptionsListItem & { key: string };

type THeaderActionsProps = {
  appSettings: AppSettings;
  tab: ConversationTab;
  language?: string;
  isVisitor: boolean;
};
